import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PERMISSION_MODULES,
  PERMISSIONS,
  type PermissionCode,
} from '@oxxean/types';
import { fetchRoles, updateRolePermissions } from '../../lib/api/admin';
import { queryKeys } from '../../lib/query-client';
import { errorMessage } from '../../lib/errors';
import { formatNumber } from '../../lib/format';
import { useAuth } from '../../hooks/useAuth';
import { useToasts } from '../../hooks/useToasts';
import type { RoleItem } from '../../types/domain';
import { DataBoundary } from '../../components/DataBoundary';
import { ConfirmDialog } from '../../components/Modal';
import { Badge, Button, Card, Notice, Skeleton } from '../../components/primitives';
import { CheckboxField } from '../../components/form';
import { PageHeading } from '../layout/PageHeading';

const MODULE_LABEL: Record<string, string> = {
  dashboard: 'Dashboard',
  indicadores: 'Indicadores',
  gestion: 'Gestión (RRLL, fiscalizaciones, hallazgos)',
  personas: 'Datos de personas',
  sincronizacion: 'Sincronización',
  administracion: 'Administración',
  auditoria: 'Auditoría y exportaciones',
};

const PERMISSION_LABEL: Partial<Record<PermissionCode, string>> = {
  [PERMISSIONS.EMPLOYEE_DETAIL_VIEW]:
    'Ficha individual: permiso propio y separado. Ver la dotación no implica ver quién la compone.',
};

/**
 * Roles y permisos.
 *
 * La matriz se construye desde `PERMISSION_MODULES` del paquete de tipos: la fuente
 * única del catálogo. Un permiso mal escrito rompe la compilación en lugar de
 * fallar en silencio.
 */
export function RolesPage(): JSX.Element {
  const { can } = useAuth();
  const { push } = useToasts();
  const queryClient = useQueryClient();
  const canManage = can(PERMISSIONS.ROLES_MANAGE);

  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [draft, setDraft] = useState<PermissionCode[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const rolesQuery = useQuery({ queryKey: queryKeys.roles, queryFn: fetchRoles });

  const roles = rolesQuery.data ?? [];
  const selected: RoleItem | undefined =
    roles.find((role) => role.code === selectedCode) ?? roles[0];

  useEffect(() => {
    if (selected !== undefined) {
      setSelectedCode(selected.code);
      setDraft(selected.permissions);
    }
  }, [selected?.code]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMutation = useMutation({
    mutationFn: ({ code, permissions }: { code: string; permissions: PermissionCode[] }) =>
      updateRolePermissions(code, permissions),
    onSuccess: () => {
      push({ kind: 'success', title: 'Permisos actualizados' });
      setConfirmOpen(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.roles });
    },
    onError: (error) => {
      push({ kind: 'error', title: 'No se pudieron guardar los permisos', message: errorMessage(error) });
    },
  });

  const dirty =
    selected !== undefined &&
    (draft.length !== selected.permissions.length ||
      draft.some((permission) => !selected.permissions.includes(permission)));

  const toggle = (permission: PermissionCode): void => {
    setDraft((current) =>
      current.includes(permission)
        ? current.filter((item) => item !== permission)
        : [...current, permission],
    );
  };

  return (
    <>
      <PageHeading
        title="Roles y permisos"
        description="Los roles son agrupaciones de permisos almacenadas en base de datos: crear uno nuevo no requiere desplegar código. La verificación ocurre en el servidor, antes del controlador."
      />

      <Notice tone="info">
        Además del permiso —que responde <em>qué</em> se puede ver— existe el ámbito, que responde{' '}
        <em>de quién</em>. El ámbito se administra por usuario, no por rol.
      </Notice>

      <DataBoundary
        isLoading={rolesQuery.isLoading}
        isError={rolesQuery.isError}
        error={rolesQuery.error}
        onRetry={() => void rolesQuery.refetch()}
        isEmpty={roles.length === 0}
        empty={{ title: 'Sin roles definidos', message: 'No hay roles registrados en la plataforma.' }}
        skeleton={
          <div className="row row--2e">
            <Skeleton height={220} radius={12} />
            <Skeleton height={220} radius={12} />
          </div>
        }
      >
        <div className="row row--2">
          <Card title="Roles definidos">
            <ul className="stack" style={{ gap: 8 }}>
              {roles.map((role) => (
                <li key={role.code}>
                  <button
                    type="button"
                    className="tile"
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      borderColor: role.code === selected?.code ? 'var(--teal)' : undefined,
                    }}
                    aria-current={role.code === selected?.code}
                    onClick={() => {
                      setSelectedCode(role.code);
                      setDraft(role.permissions);
                    }}
                  >
                    <span className="inline" style={{ justifyContent: 'space-between' }}>
                      <span className="tile__name">{role.name}</span>
                      <span className="inline">
                        {role.system && <Badge tone="neutral">Sistema</Badge>}
                        <Badge tone="blue">Nivel {role.hierarchyLevel}</Badge>
                      </span>
                    </span>
                    <span className="tile__desc">
                      {role.description ?? 'Sin descripción.'} · {formatNumber(role.permissions.length)}{' '}
                      permisos · {formatNumber(role.userCount)} usuario(s)
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          {selected !== undefined && (
            <Card
              title={`Permisos de ${selected.name}`}
              actions={
                canManage ? (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={!dirty}
                      onClick={() => setDraft(selected.permissions)}
                    >
                      Descartar
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={!dirty}
                      onClick={() => setConfirmOpen(true)}
                    >
                      Guardar
                    </Button>
                  </>
                ) : undefined
              }
            >
              {!canManage && (
                <Notice tone="neutral">
                  Su perfil permite consultar la composición de los roles, pero no modificarla.
                </Notice>
              )}

              <div className="stack">
                {Object.entries(PERMISSION_MODULES).map(([moduleKey, permissions]) => (
                  <fieldset className="fieldset" key={moduleKey}>
                    <legend>{MODULE_LABEL[moduleKey] ?? moduleKey}</legend>
                    <div className="stack" style={{ gap: 6 }}>
                      {permissions.map((permission) => (
                        <div key={permission}>
                          <CheckboxField
                            label={<span className="mono">{permission}</span>}
                            checked={draft.includes(permission)}
                            disabled={!canManage || selected.system}
                            onChange={() => toggle(permission)}
                          />
                          {PERMISSION_LABEL[permission] !== undefined && (
                            <p className="tiny muted" style={{ marginLeft: 23 }}>
                              {PERMISSION_LABEL[permission]}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </fieldset>
                ))}
              </div>

              {selected.system && (
                <Notice tone="neutral">
                  Este es un rol del sistema: su composición está fijada por el catálogo del proyecto y
                  no se edita desde la interfaz.
                </Notice>
              )}
            </Card>
          )}
        </div>
      </DataBoundary>

      <ConfirmDialog
        open={confirmOpen}
        title="Guardar cambios de permisos"
        confirmLabel="Guardar"
        loading={saveMutation.isPending}
        message={`Los cambios afectarán de inmediato a ${formatNumber(selected?.userCount ?? 0)} usuario(s) con el rol ${selected?.name ?? ''}.`}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          if (selected !== undefined) {
            saveMutation.mutate({ code: selected.code, permissions: draft });
          }
        }}
      />
    </>
  );
}
